import { TabsContent } from "@/components/ui/tabs";
import useSettings from "@/hooks/useSettings";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { getTopSellingSettings, updateTopSellingDaySettings, clearNewArrivalItems as clearNewArrivalItemsApi } from "@/api/settings/take-order-settings";
import { clearNewArrivalItemsCache } from "@/redux/slice/take-order-slice/home-page-items-slice";
import useAuth from "@/hooks/useAuth";
import { DeleteConfirmDialog } from "@/components/dialog/common-dialog";

const DAY_OPTIONS = [
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
  { value: 7, label: "Sunday" },
];

function HomePageTab() {
  const { settings } = useSettings();
  const [selectedDay, setSelectedDay] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isClearDialogOpen, setIsClearDialogOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const storedSettings = settings || JSON.parse(localStorage.getItem("settings") || "{}");
    const savedDay =
      storedSettings?.take_order?.home_page_setting?.top_selling_day ||
      storedSettings?.take_order?.home_page_setting?.top_selling_date ||
      storedSettings?.top_selling_day ||
      1;

    setSelectedDay(Number(savedDay));
  }, [settings]);

  useEffect(() => {
    const loadServerSettings = async () => {
      if (!user?.restaurant_id) return;
      const response = await getTopSellingSettings(user.restaurant_id);
      if (response?.status === "success" && response?.data?.top_selling_setting?.day_id) {
        setSelectedDay(Number(response.data.top_selling_setting.day_id));
      }
    };

    loadServerSettings();
  }, [user]);

  const handleDayChange = async (dayValue) => {
    setSelectedDay(dayValue);
    const restaurant_id = user?.restaurant_id;
    if (!restaurant_id) {
      toast.error("Restaurant ID not found.");
      return;
    }

    setLoading(true);
    const response = await updateTopSellingDaySettings(restaurant_id, {
      day_id: dayValue,
    });
    setLoading(false);

    if (response?.status === "success" || response?.status === "ok") {
      toast.success("Date saved to server.");
      const currentSettings = JSON.parse(localStorage.getItem("settings") || "{}");
      localStorage.setItem(
        "settings",
        JSON.stringify({
          ...currentSettings,
          take_order: {
            ...currentSettings.take_order,
            home_page_setting: {
              ...currentSettings.take_order?.home_page_setting,
              top_selling_day: dayValue,
            },
          },
        })
      );
    } else {
      toast.error(response?.message || "Failed to save date.");
    }
  };

  const clearNewArrivalItems = async () => {
    const restaurant_id = user?.restaurant_id;
    if (!restaurant_id) {
      toast.error("Restaurant ID not found.");
      return;
    }

    const response = await clearNewArrivalItemsApi(restaurant_id);
    if (response?.status === "success" || response?.status === "ok") {
      clearNewArrivalItemsCache();
      toast.success("New arrival items cleared.");
    } else {
      toast.error(response?.message || "Failed to clear new arrival items.");
    }
  };

  return (
    <TabsContent value="home-page" className="w-full">
      <div className="w-full p-2 flex flex-col gap-4">
        <div className="w-full p-4 bg-color2 text-color5 rounded-md">
          <div className="flex items-center gap-1">
            <span className="text-base font-semibold">Top Selling Items</span>
            <span className="text-sm text-color6"> ( Select one weekday to show new changes )</span>
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {DAY_OPTIONS.map((day) => (
              <label
                key={day.value}
                htmlFor={`top-selling-day-${day.value}`}
                className={`block cursor-pointer rounded-xl border p-3 transition duration-150 ${
                  selectedDay === day.value
                    ? "border-green-500 bg-green-500/10 text-green-100"
                    : "border-neutral-700 bg-neutral-900/50 hover:border-neutral-500"
                }`}
              >
                <input
                  id={`top-selling-day-${day.value}`}
                  type="radio"
                  name="top-selling-day"
                  value={day.value}
                  checked={selectedDay === day.value}
                  onChange={() => handleDayChange(day.value)}
                  className="sr-only"
                />
                <div className="flex items-center justify-between gap-3">
                  <span>{day.label}</span>
                  {selectedDay === day.value && (
                    <span className="rounded-full bg-green-600/80 px-2 py-1 text-[11px] uppercase tracking-[0.1em] text-white">
                      Selected
                    </span>
                  )}
                </div>
              </label>
            ))}
          </div>

          <div className="mt-3 text-sm text-color8">
            {loading ? "Saving selection..." : "Only one day can be selected."}
          </div>
        </div>
        <div className="w-full p-4 bg-color2 text-color5 rounded-md">
          <div className="flex items-center gap-1">
            <span className="text-base font-semibold">New Arrival Items</span>
            <span className="text-sm text-color6"> ( New Arrival Items will be cleared and it will show again when new menu item is added )</span>
          </div>
          <button
            className="mt-2 rounded-md bg-color8 px-4 py-2 text-sm font-medium text-color5 hover:bg-color8/80"
            onClick={() => setIsClearDialogOpen(true)}
          >
            Clear New Arrival Items
          </button>
          <DeleteConfirmDialog
            isOpen={isClearDialogOpen}
            onClose={() => setIsClearDialogOpen(false)}
            onConfirm={clearNewArrivalItems}
            title="Clear New Arrival Items"
            description="Do you really want to clear all new arrival items? This cannot be undone."
            yesBtnTxt="Yes, clear"
            noBtnTxt="Cancel"
            yesBtnClass="bg-color8"
          />
        </div>
      </div>
    </TabsContent>
  );
}

export default HomePageTab;
